<script setup lang="ts">
import { computed } from 'vue';
import { EMPTY_FIELD_SIGN } from '../forms/utils';
import { I18nKey } from '@/utils/typings';
import {
  DefinitionDto,
  NumericValueDto,
  TaskItemReferenceMeasurementResultsDto,
} from '@/api/generator';
import NumericValueField from '../forms/dataTypes/NumericValueField.vue';
import { translateEntityProperty, truthy } from '@/utils/helper';
import { useTranslation } from 'i18next-vue';
import { asyncComputed } from '@vueuse/core';
import { api } from '@/api';
import InfoMessage from '../InfoMessage/InfoMessage.vue';

const { t } = useTranslation();

const props = defineProps<{
  value?: TaskItemReferenceMeasurementResultsDto;
}>();

const lastResult = computed(() => props.value?.results ?? undefined);

const definitions = asyncComputed<Record<string, DefinitionDto[]> | undefined>(async () => {
  const ids = props.value?.results?.map((quantity) => quantity.dataTypeId).filter(truthy);

  if (ids?.length) {
    const { result } = await api.numericQuantitiesApi.getDefinitionCatalogs({
      requestBody: [...ids],
    });

    return result;
  }
});

async function getHint(evaluation: NumericValueDto) {
  if (evaluation.dataTypeId) {
    const nonSpecificDoseDefinition = definitions.value?.[evaluation.dataTypeId]?.find(
      (definition) => definition.definitionLabel === 'NonSpecificDose',
    );

    if (nonSpecificDoseDefinition) {
      return {
        title: t(nonSpecificDoseDefinition.name as I18nKey),
        message: t(nonSpecificDoseDefinition.description as I18nKey),
      };
    }
  }
}

const items = asyncComputed(async () => {
  return await Promise.all(
    lastResult.value?.map(async (quantity) => ({
      title: [
        translateEntityProperty('name', quantity) ?? EMPTY_FIELD_SIGN,
        translateEntityProperty('name', quantity.geometry) ?? '',
      ].join(' '),
      hint: await getHint(quantity),
      quantity: quantity,
    })) ?? [],
  );
});

const headers = [
  {
    title: t('Quantity_one'),
    key: 'title',
    sortable: false,
  },
  {
    title: t('EvaluationType_one'),
    key: 'evaluationType',
    sortable: false,
  },
  {
    title: t('Value'),
    key: 'quantity',
    align: 'end',
    sortable: false,
  },
] as const;
</script>

<template>
  <v-data-table
    v-if="lastResult"
    :headers="headers"
    :items="items"
    :itemsPerPage="-1"
    hideDefaultFooter
  >
    <template #item.title="{ item: { hint, title } }">
      <v-label class="text-justify">{{ title }}</v-label>
      <InfoMessage v-if="hint" maxWidth="400px">
        <div class="text-h6">{{ hint.title }}</div>
        {{ hint.message }}
      </InfoMessage>
    </template>

    <template
      #item.evaluationType="{
        item: {
          quantity: { evaluationType },
        },
      }"
    >
      {{ $t(`EvaluationTypeEnum.${evaluationType}` as I18nKey) }}
    </template>

    <template #item.quantity="{ value: quantity }">
      <NumericValueField :modelValue="quantity" mode="details" class="justify-end" />
    </template>
  </v-data-table>
</template>

<style scoped lang="scss">
:deep(.v-data-table__td) {
  vertical-align: top;
  padding-left: 0 !important;
  padding-top: 8px !important;
  padding-bottom: 8px !important;
  min-height: unset !important;
  height: unset !important;
}
</style>
